import React, { createContext, useContext, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';

interface ChecklistSeguridad {
  id: string;
  cliente_id: string;
  tecnico_id: string;
  fecha: string;
  estado: string;
  observaciones_generales?: string;
  firma_tecnico?: string;
  created_at: string;
  updated_at: string;
}

interface ChecklistRespuesta {
  id: string;
  checklist_id: string;
  bloque_id: string;
  pregunta_id: string;
  pregunta_texto: string;
  respuesta?: 'si' | 'no' | 'na';
  observaciones?: string;
  fotos: any[];
  created_at: string;
  updated_at: string;
}

interface ChecklistContextType {
  crearChecklist: (clienteId: string, respuestas: any[], firma?: string, observaciones?: string, tipoRevision?: string) => Promise<{ error: any; data?: any }>;
  obtenerChecklistsCliente: (clienteId: string) => Promise<{ error: any; data?: any[] }>;
  obtenerTodosChecklists: () => Promise<{ error: any; data?: any[] }>;
  obtenerRespuestasChecklist: (checklistId: string) => Promise<{ error: any; data?: any[] }>;
  actualizarRespuestasChecklist: (checklistId: string, respuestas: any[], firma?: string) => Promise<{ error: any }>;
  isLoading: boolean;
}

const ChecklistContext = createContext<ChecklistContextType | undefined>(undefined);

export const useChecklist = () => {
  const context = useContext(ChecklistContext);
  if (context === undefined) {
    throw new Error('useChecklist must be used within a ChecklistProvider');
  }
  return context;
};

export const ChecklistProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { perfil } = useAuth();

  const crearChecklist = async (
    clienteId: string, 
    respuestas: any[], 
    firma?: string, 
    observaciones?: string,
    tipoRevision?: string
  ) => {
    if (!perfil) {
      return { error: { message: 'Usuario no autenticado' } };
    }

    setIsLoading(true);
    try {
      // Crear el checklist principal
      const { data: checklist, error: checklistError } = await supabase
        .from('checklists_seguridad')
        .insert({
          cliente_id: clienteId,
          tecnico_id: perfil.user_id,
          estado: 'completado',
          observaciones_generales: observaciones,
          firma_tecnico: firma,
          tipo_revision: tipoRevision || 'trimestral'
        } as any)
        .select()
        .single();

      if (checklistError) {
        console.error('Error creando checklist:', checklistError);
        return { error: checklistError };
      }

      // Crear las respuestas individuales
      const respuestasFormatted = respuestas.map(respuesta => ({
        checklist_id: checklist.id,
        bloque_id: respuesta.bloque_id,
        pregunta_id: respuesta.pregunta_id,
        pregunta_texto: respuesta.pregunta_texto,
        respuesta: respuesta.respuesta,
        observaciones: respuesta.observaciones,
        fotos: JSON.stringify(respuesta.fotos || [])
      }));

      const { error: respuestasError } = await supabase
        .from('checklist_respuestas')
        .insert(respuestasFormatted);

      if (respuestasError) {
        console.error('Error guardando respuestas:', respuestasError);
        // Si falla guardando respuestas, eliminamos el checklist
        await supabase.from('checklists_seguridad').delete().eq('id', checklist.id);
        return { error: respuestasError };
      }

      return { error: null, data: checklist };
    } catch (error) {
      console.error('Error inesperado:', error);
      return { error };
    } finally {
      setIsLoading(false);
    }
  };

  const obtenerChecklistsCliente = async (clienteId: string) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('checklists_seguridad')
        .select(`
          *,
          perfiles!checklists_seguridad_tecnico_id_fkey(nombre)
        `)
        .eq('cliente_id', clienteId)
        .order('created_at', { ascending: false });

      return { error, data };
    } catch (error) {
      console.error('Error obteniendo checklists:', error);
      return { error };
    } finally {
      setIsLoading(false);
    }
  };

  const obtenerTodosChecklists = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('checklists_seguridad')
        .select(`
          *,
          perfiles!checklists_seguridad_tecnico_id_fkey(nombre),
          clientes!checklists_seguridad_cliente_id_fkey(nombre)
        `)
        .order('created_at', { ascending: false });

      return { error, data };
    } catch (error) {
      console.error('Error obteniendo todos los checklists:', error);
      return { error };
    } finally {
      setIsLoading(false);
    }
  };

  const obtenerRespuestasChecklist = async (checklistId: string) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('checklist_respuestas')
        .select('*')
        .eq('checklist_id', checklistId)
        .order('bloque_id', { ascending: true })
        .order('pregunta_id', { ascending: true });

      if (data) {
        // Parsear las fotos de JSON string a array
        const formattedData = data.map(respuesta => ({
          ...respuesta,
          fotos: JSON.parse((respuesta.fotos as string) || '[]')
        }));
        return { error, data: formattedData };
      }

      return { error, data };
    } catch (error) {
      console.error('Error obteniendo respuestas:', error);
      return { error };
    } finally {
      setIsLoading(false);
    }
  };

  const actualizarRespuestasChecklist = async (checklistId: string, respuestas: any[], firma?: string) => {
    setIsLoading(true);
    try {
      // Actualizar firma si se proporciona
      if (firma) {
        const { error: firmaError } = await supabase
          .from('checklists_seguridad')
          .update({ firma_tecnico: firma })
          .eq('id', checklistId);

        if (firmaError) {
          console.error('Error actualizando firma:', firmaError);
          return { error: firmaError };
        }
      }

      // Eliminar respuestas existentes
      const { error: deleteError } = await supabase
        .from('checklist_respuestas')
        .delete()
        .eq('checklist_id', checklistId);

      if (deleteError) {
        console.error('Error eliminando respuestas:', deleteError);
        return { error: deleteError };
      }

      // Crear las respuestas actualizadas
      const respuestasFormatted = respuestas.map(respuesta => ({
        checklist_id: checklistId,
        bloque_id: respuesta.bloque_id,
        pregunta_id: respuesta.pregunta_id,
        pregunta_texto: respuesta.pregunta_texto,
        respuesta: respuesta.respuesta,
        observaciones: respuesta.observaciones,
        fotos: JSON.stringify(respuesta.fotos || [])
      }));

      const { error: insertError } = await supabase
        .from('checklist_respuestas')
        .insert(respuestasFormatted);

      if (insertError) {
        console.error('Error guardando respuestas actualizadas:', insertError);
        return { error: insertError };
      }

      return { error: null };
    } catch (error) {
      console.error('Error actualizando checklist:', error);
      return { error };
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    crearChecklist,
    obtenerChecklistsCliente,
    obtenerTodosChecklists,
    obtenerRespuestasChecklist,
    actualizarRespuestasChecklist,
    isLoading
  };

  return (
    <ChecklistContext.Provider value={value}>
      {children}
    </ChecklistContext.Provider>
  );
};

export type { ChecklistSeguridad, ChecklistRespuesta };